import { Flex, Popover, Skeleton, Text } from "@/atoms"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { useHttp } from "@/hooks/useHttp"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useTranslation } from "@/hooks/useTranslation"
import { IconResource } from "@/molecules/Resources"
import { Form } from "@/organisms/Form"
import { AlertConfigurationTabVirtualMachineDrawer } from "../AlertConfigurationTabVirtualMachineDrawer"

import styles from "./ModalConfigurationTabVirtualMachineDrawer.module.scss"

import type { Project } from "@/organisms/Drawers/applications/inventory/useInventory.types"
import type { CorrelatedImagesModalConfigurationTabVirtualMachineDrawerProps } from "./ModalConfigurationTabVirtualMachineDrawer.types"

export const CorrelatedImagesModalConfigurationTabVirtualMachineDrawer = (
  props: CorrelatedImagesModalConfigurationTabVirtualMachineDrawerProps
) => {
  const { t } = useTranslation()
  const { t: tModal } = useTranslation("translation", {
    keyPrefix: "inventory.vm.drawer.configuration.modals.resetImages"
  })
  const { form } = Form.useForm()
  const project = Form.useWatch("project", form) as
    | { data: Project }
    | undefined
  const correlations = (project?.data?.correlations ?? [])
    .filter((c) => c.artifactType === "ARTIFACT_TYPE_IMAGE")
    .map((c) => c.artifactId)
  const { images = correlations } = props
  const VISIBLE_COUNT = 3
  const [visible, rest] = [
    images.slice(0, VISIBLE_COUNT),
    images.slice(VISIBLE_COUNT)
  ]

  const restCount = rest.length
  const hasRest = restCount > 0

  const countContext = useHttp<{ data: number }>(
    `${RESOURCES_NAMES.TRIAGE.ISSUES_COUNT}`,
    { imageId: images.join(",") },
    { staleTime: 0, gcTime: 0, enabled: images.length > 0 }
  )
  const convertors = useNumberConvertor()

  if (countContext.isPending) return <Skeleton active />

  const issueCount = countContext.response?.data || 0

  return (
    <Flex vertical gap={10}>
      <Text>{tModal("text1")}</Text>
      <Flex vertical>
        <Flex vertical gap={4} justify="center">
          {visible.map((name, index) => (
            <Flex
              key={index}
              gap={10}
              align="center"
              className={styles.correlatedImagesItem}
            >
              <IconResource type="ImageRepository" imageSource="kodem" />
              <Text>{name}</Text>
            </Flex>
          ))}
          {hasRest && (
            <Popover
              content={
                <Flex vertical gap={4} justify="center">
                  {rest.map((name, index) => (
                    <Flex
                      key={index}
                      gap={10}
                      align="center"
                      className={styles.correlatedImagesItem}
                    >
                      <IconResource
                        type="ImageRepository"
                        imageSource="kodem"
                      />
                      <Text>{name}</Text>
                    </Flex>
                  ))}
                </Flex>
              }
            >
              <Text type="secondary">{`+${restCount} ${t("general.image", { count: restCount })}`}</Text>
            </Popover>
          )}
        </Flex>
      </Flex>
      <Text>{tModal("text2")}</Text>
      <AlertConfigurationTabVirtualMachineDrawer
        type="warning"
        message={tModal("text3", {
          count: issueCount,
          number: convertors.abbreviateNumber(issueCount)
        })}
      />
    </Flex>
  )
}
